/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useMemo, useEffect } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Field, Form as FinalForm, FormSpy, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { Form, IconChecked, IconCheckmark, RangeSlider } from '../../../components';

import css from './PriceFilterForm.module.css';

const DEBOUNCE_WAIT_TIME = 400;

// Utility to format number with commas
const formatNumber = value => {
  if (value === '' || value == null) return '';
  const num = Number(value);
  return isNaN(num) ? '' : num.toLocaleString();
};

// Utility to strip commas and parse to int
// Utility to strip non-digit characters and parse to int
const parseNumber = value => {
  if (value === '' || value == null) return '';
  if (typeof value === 'string') {
    // remove everything except digits and optional leading minus sign
    const raw = value.replace(/[^\d-]/g, '').trim();
    if (raw === '') return '';
    const num = parseInt(raw, 10);
    return Number.isNaN(num) ? '' : num;
  }
  // if already a number
  if (typeof value === 'number') return Number.isNaN(value) ? '' : value;
  return '';
};
const validatePriceRange = values => {
  const errors = {};
  const min = parseNumber(values.minPrice) || 0;
  const max = parseNumber(values.maxPrice) || Number.MAX_VALUE;

  if (min !== '' && max !== '' && min > max) {
    const msgId = 'RangeFilter.invalidRange';

    // optional: keep a form-level entry so you can render one message below both inputs
    errors.priceRange = msgId;
  }

  return errors;
};
export const PriceFilterFields = props => {
  const {
    id,
    min = 0,
    max = 1000,
    step = 1,
    showAsPopup = false,
    isOpen = false,
    isInSideBar = false,
    hideActions = false,
    style,
    className,
    contentRef,
    liveEdit,
    onClear,
    onCancel,
    children,
    handleSubmit,
    handleChange,
    currency,
  } = props;

  const form = useForm();
  // subscribe to values and invalid so we can disable submit / read values
  const { values, invalid, errors } = useFormState({
    subscription: { values: true, invalid: true, errors: true },
  });

  const intl = useIntl();

  const minPriceRaw = values.minPrice;
  const maxPriceRaw = values.maxPrice;
  const minPrice = typeof minPriceRaw !== 'string' ? minPriceRaw : min;
  const maxPrice = typeof maxPriceRaw !== 'string' ? maxPriceRaw : max;

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const clearText = intl.formatMessage({ id: 'PriceFilterForm.reset' });
  const cancelText = intl.formatMessage({ id: 'PriceFilterForm.cancel' });
  const submitText = intl.formatMessage({ id: 'PriceFilterForm.submit' });

  const classes = classNames(css.root, className, {
    [css.popup]: showAsPopup,
    [css.isOpenAsPopup]: showAsPopup && isOpen,
    [css.plain]: !showAsPopup,
    [css.isOpen]: !showAsPopup && isOpen,
  });
  return (
    <div className={classes} style={{ minWidth: '330px', ...style }} ref={contentRef}>
      <div className={css.contentWrapper}>
        {showAsPopup && (
          <span className={css.label}>
            <FormattedMessage id="PriceFilterForm.label" />
          </span>
        )}
        <div className={css.inputsWrapper}>
          <div className={css.inputRow}>
            <label htmlFor={`${id}.minPrice`}>Min</label>
            <span>€</span>
            <Field
              className={classNames(css.minPrice, { [css.priceInSidebar]: isInSideBar })}
              id={`${id}.minPrice`}
              name="minPrice"
              parse={parseNumber}
              format={formatNumber}
            >
              {({ input, meta }) => (
                <>
                  <input
                    {...input}
                    type="text"
                    placeholder={formatNumber(min)}
                    step={step}
                    onChange={e => input.onChange(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && hideActions) {
                        e.preventDefault();
                      }
                    }}
                  />
                  {meta.error && (meta.touched || meta.submitFailed) && (
                    <div className={css.error}>{meta.error}</div>
                  )}
                </>
              )}
            </Field>
          </div>

          <div className={css.inputRow}>
            <label htmlFor={`${id}.maxPrice`}>Max</label>
            <span>€</span>
            <Field
              className={classNames(css.maxPrice, { [css.priceInSideBar]: isInSideBar })}
              id={`${id}.maxPrice`}
              name="maxPrice"
              parse={parseNumber}
              format={formatNumber}
            >
              {({ input, meta }) => (
                <>
                  <input
                    {...input}
                    type="text"
                    placeholder={'-'}
                    step={step}
                    onChange={e => input.onChange(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && hideActions) {
                        e.preventDefault();
                      }
                    }}
                  />
                  {meta.error && (meta.touched || meta.submitFailed) && (
                    <div className={css.error}>{meta.error}</div>
                  )}
                </>
              )}
            </Field>
          </div>
          {errors?.priceRange && (
            <div className={css.error}>{intl.formatMessage({ id: errors.priceRange })}</div>
          )}
          <div className={css.divider} />
        </div>
      </div>

      <div className={css.presetsTitle}>
        <FormattedMessage id="Global.mostFrequent" />
      </div>

      <div className={css.presets}>
        {[
          [0, 10000],
          [10000, 25000],
          [25000, 50000],
          [50000, 100000],
          [100000, 250000],
          [250000, max],
        ].map(([minVal, maxVal], i) => {
          return (
            <button
              key={i}
              type="button"
              onClick={() => {
                form.change('minPrice', minVal);
                if (maxVal !== max) form.change('maxPrice', maxVal);
                else form.change('maxPrice', undefined);
              }}
              className={css.presetButton}
            >
              {(minVal === minPrice || (minVal === 0 && !minPrice)) &&
                (maxVal === maxPrice || (maxVal === max && !maxPrice)) && (
                  <IconCheckmark size="small" />
                )}
              {maxVal === max
                ? `${minVal.toLocaleString()}+`
                : `${minVal.toLocaleString()} - ${maxVal.toLocaleString()}`}
            </button>
          );
        })}
      </div>

      {liveEdit ? (
        <FormSpy
          onChange={handleChange}
          subscription={{ values: true, dirty: true, invalid: true }}
        />
      ) : !hideActions ? (
        <div className={css.buttonsWrapper}>
          <button className={css.clearButton} type="button" onClick={onClear}>
            {clearText}
          </button>
          <button className={css.cancelButton} type="button" onClick={handleCancel}>
            {cancelText}
          </button>
          <button className={css.submitButton} type="submit" disabled={invalid}>
            {submitText}
          </button>
        </div>
      ) : null}

      {children}
    </div>
  );
};

const PriceFilterForm = props => {
  const {
    liveEdit = false,
    onChange,
    onSubmit,
    onCancel,
    onClear,
    standalone = true,
    hideActions = false,
    intl: injectedIntl,
    ...rest
  } = props;

  const intl = injectedIntl || useIntl();

  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  // memoize debounced change handler so it's stable between renders
  const handleChange = useMemo(
    () =>
      debounce(formState => {
        if (validatePriceRange(formState.values).priceRange) return; // formState will now contain invalid if FormSpy subscription includes it
        if (formState && formState.dirty && !formState.invalid) {
          const { minPrice, maxPrice, ...restValues } = formState.values || {};

          onChange({
            ...(minPrice && {
              minPrice: minPrice === '' || minPrice == null ? rest.min ?? 0 : minPrice,
            }),
            ...(maxPrice && {
              maxPrice: maxPrice === '' || maxPrice == null ? rest.max ?? 0 : maxPrice,
            }),
            ...restValues,
          });
        }
      }, DEBOUNCE_WAIT_TIME),
    // dependencies: onChange and the default min/max from props (rest)
    [onChange, rest.min, rest.max]
  );

  // cancel debounce on unmount
  useEffect(() => {
    return () => {
      handleChange.cancel();
    };
  }, [handleChange]);

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    return onSubmit({
      minPrice: minPrice === '' ? rest.min : minPrice,
      maxPrice: maxPrice === '' ? rest.max : maxPrice,
      ...restValues,
    });
  };

  if (!standalone) {
    return (
      <PriceFilterFields
        {...props}
        intl={intl}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
    );
  }
  return (
    <FinalForm
      {...rest}
      onSubmit={liveEdit ? () => null : handleSubmit}
      validate={validatePriceRange}
      render={formRenderProps => (
        <Form onSubmit={formRenderProps.handleSubmit}>
          <PriceFilterFields
            {...props}
            {...formRenderProps}
            intl={intl}
            handleChange={handleChange}
            handleSubmit={formRenderProps.handleSubmit}
          />
        </Form>
      )}
    />
  );
};

export default PriceFilterForm;
